import type { FederatedPointerEvent } from "pixi.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getMapPointerPosition } from "../getMapPointerPosition";
import type { ImageOverlayGeometry } from "../imageOverlayGeometry";
import {
    findNearestContainingOverlay,
    ImageOverlaysHitArea,
} from "../imageOverlayGeometry";
import type { InteractionTarget } from "../managers/InteractionManager";
import type {
    ImageOverlayMapObject,
    MapItemHandler,
    MapPointerEvent,
} from "../types";
import { useScene } from "./useScene";

interface Options {
    id: string;
    geometries: readonly ImageOverlayGeometry[];
    onItemClick?: MapItemHandler<ImageOverlayMapObject>;
}

export function useImageOverlayInteractions({
    id,
    geometries,
    onItemClick,
}: Options) {
    const { coordinates, interactionManager, viewport } = useScene();
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const interactionTargetsRef = useRef(new Map<string, InteractionTarget>());
    const interactiveGeometries = useMemo(
        () =>
            geometries.filter(
                (geometry) => geometry.item.interactive !== false,
            ),
        [geometries],
    );

    useEffect(() => {
        const interactiveIds = new Set(
            interactiveGeometries.map((geometry) => geometry.item.id),
        );

        for (const [itemId, target] of interactionTargetsRef.current) {
            if (interactiveIds.has(itemId)) continue;

            interactionManager.removeTarget(target);
            interactionTargetsRef.current.delete(itemId);
        }

        setHoveredId((current) =>
            current !== null && !interactiveIds.has(current) ? null : current,
        );
    }, [interactionManager, interactiveGeometries]);

    useEffect(
        () => () => {
            for (const target of interactionTargetsRef.current.values()) {
                interactionManager.removeTarget(target);
            }
        },
        [interactionManager],
    );

    const hitArea = useMemo(
        () =>
            new ImageOverlaysHitArea(
                interactiveGeometries.map((geometry) => geometry.sceneCorners),
            ),
        [interactiveGeometries],
    );

    const getPointerPosition = useCallback(
        (event: FederatedPointerEvent) =>
            getMapPointerPosition(event, viewport, coordinates),
        [coordinates, viewport],
    );

    const resolveOverlayAtPointer = useCallback(
        (event: FederatedPointerEvent) => {
            const pointerPosition = getPointerPosition(event);
            return pointerPosition
                ? findNearestContainingOverlay(
                      interactiveGeometries,
                      pointerPosition.worldPosition,
                  )
                : undefined;
        },
        [getPointerPosition, interactiveGeometries],
    );

    const getInteractionTarget = useCallback(
        (itemId: string): InteractionTarget => {
            const existing = interactionTargetsRef.current.get(itemId);
            if (existing) return existing;

            const target: InteractionTarget = {};
            interactionTargetsRef.current.set(itemId, target);
            return target;
        },
        [],
    );

    const configureHoverTarget = useCallback(
        (candidate: ImageOverlayGeometry) => {
            const itemId = candidate.item.id;
            const target = getInteractionTarget(itemId);

            target.onHoverChange = (hovered) => {
                if (hovered) {
                    setHoveredId(itemId);
                } else {
                    setHoveredId((current) =>
                        current === itemId ? null : current,
                    );
                }
            };
            return target;
        },
        [getInteractionTarget],
    );

    const handlePointerOver = useCallback(
        (event: FederatedPointerEvent) => {
            const candidate = resolveOverlayAtPointer(event);
            if (!candidate) return;

            interactionManager.pointerOver(
                event,
                configureHoverTarget(candidate),
            );
        },
        [configureHoverTarget, interactionManager, resolveOverlayAtPointer],
    );

    const handlePointerMove = useCallback(
        (event: FederatedPointerEvent) => {
            const candidate = resolveOverlayAtPointer(event);
            if (!candidate) return;

            interactionManager.pointerMove(
                event,
                configureHoverTarget(candidate),
            );
        },
        [configureHoverTarget, interactionManager, resolveOverlayAtPointer],
    );

    const handlePointerOut = useCallback(
        (event: FederatedPointerEvent) => {
            interactionManager.pointerOut(event);
        },
        [interactionManager],
    );

    const handlePointerTap = useCallback(
        (event: FederatedPointerEvent) => {
            const pointerPosition = getPointerPosition(event);
            if (!pointerPosition) return;

            const clickedGeometry = findNearestContainingOverlay(
                interactiveGeometries,
                pointerPosition.worldPosition,
            );
            if (!clickedGeometry) return;

            const target = getInteractionTarget(clickedGeometry.item.id);
            const mapEvent: MapPointerEvent<ImageOverlayMapObject> = {
                item: clickedGeometry.item,
                objectRef: { layerId: id, id: clickedGeometry.item.id },
                ...pointerPosition,
                ctrlKey: event.ctrlKey,
                metaKey: event.metaKey,
            };

            target.onClick = onItemClick
                ? () => onItemClick(mapEvent)
                : undefined;
            interactionManager.pointerTap(event, target);
        },
        [
            getPointerPosition,
            getInteractionTarget,
            id,
            interactionManager,
            interactiveGeometries,
            onItemClick,
        ],
    );

    const surfaceProps = useMemo(
        () => ({
            hitArea,
            cursor: onItemClick ? "pointer" : undefined,
            onPointerOver: handlePointerOver,
            onPointerMove: handlePointerMove,
            onPointerOut: handlePointerOut,
            onPointerTap: handlePointerTap,
        }),
        [
            handlePointerMove,
            handlePointerOut,
            handlePointerOver,
            handlePointerTap,
            hitArea,
            onItemClick,
        ],
    );

    return { hoveredId, surfaceProps };
}
